/**
 * Modal di conferma per gli inneschi manuali (SPEC-014.2 §A.5).
 *
 * Un solo componente per i due pulsanti — «Resoconto ora» e «Genera adesso» —
 * perché la spunta è la stessa decisione; cambia solo la catena che descrive, e
 * quella arriva come prop.
 *
 * ## Le tre cose che questo modal esiste per dire
 *
 * 1. **Cosa sta per succedere davvero.** Per «Resoconto ora» la spunta NON
 *    significa "esegui l'auto-trading su questo resoconto": un briefing di
 *    mercato produce prosa e analisi, non proposte. Significa *al termine,
 *    genera anche il digest di portafoglio e valuta l'auto-trading* — tre
 *    passaggi e ~20 minuti. Una promessa del genere formulata male è il difetto
 *    già pagato in F-037.
 * 2. **La ragione vera quando non si può.** Il flag globale spento e i
 *    portafogli tutti su «Spento» sono due problemi diversi con due
 *    interruttori diversi: dare la ragione sbagliata manda l'utente a cercare
 *    dove non c'è nulla da cambiare. Il controllo resta **visibile e
 *    disabilitato con la ragione**, mai nascosto (precedente di F-051: «una
 *    funzione invisibile non si può nemmeno desiderare»).
 * 3. **Che verranno inviati ordini reali**, quando è così, con l'elenco dei
 *    portafogli e un pulsante che riporta l'azione — «Genera ed esegui», non un
 *    «Conferma» generico identico nei tre casi.
 */

import { useEffect, useState } from 'react';
import { Alert, Button, Modal } from '@/ui';
import { useAutoTradingReadiness } from './api';
import './auto-trading.css';

export type AutoTradingConfirmModalProps = {
  title: string;
  /** Cosa produce l'azione di per sé, indipendentemente dalla spunta. */
  summary: string;
  /**
   * `chain` = la spunta innesca una CATENA (briefing di mercato → digest →
   * valutazione), non l'auto-trading diretto. `direct` = il digest è generato
   * qui e la valutazione parte subito dopo.
   */
  kind: 'chain' | 'direct';
  confirmLabel: string;
  pending?: boolean;
  onConfirm: (runAutoTrading: boolean) => void;
  onClose: () => void;
};

export function AutoTradingConfirmModal({
  title,
  summary,
  kind,
  confirmLabel,
  pending = false,
  onConfirm,
  onClose,
}: AutoTradingConfirmModalProps) {
  const readiness = useAutoTradingReadiness();
  // **Mai selezionata per difetto**, in nessuno dei tre stati: è il gesto che
  // autorizza un ordine, e un default acceso lo trasformerebbe in un'omissione.
  const [checked, setChecked] = useState(false);

  const data = readiness.data;
  const available = data?.canRunAutoTrading ?? false;

  // Se la prontezza cambia mentre il modal è aperto (un'altra scheda che spegne
  // un portafoglio), una spunta rimasta accesa prometterebbe qualcosa che il
  // server rifiuterebbe comunque.
  useEffect(() => {
    if (!available) setChecked(false);
  }, [available]);

  const isExecute = data?.effectiveMode === 'execute';
  const armed = checked && available;

  const checkboxLabel = !available
    ? 'Auto-trading non attivo.'
    : isExecute
      ? 'Esegui anche le azioni di auto-trading.'
      : 'Valuta anche le azioni di auto-trading.';

  const consequence = !available
    ? (data?.blockedDetail ?? 'Verifica le impostazioni di auto-trading.')
    : isExecute
      ? `Verranno inviati ordini reali sui portafogli: ${data?.executePortfolioNames.join(', ')}. Restano applicati i limiti operativi.`
      : 'Le proposte saranno registrate ma nessun ordine verrà inviato (modalità prova).';

  return (
    <Modal title={title} onClose={onClose}>
      <div className="autotrade-confirm">
        <p className="autotrade-confirm__summary">{summary}</p>

        <label className={`autotrade-confirm__check ${!available ? 'autotrade-confirm__check--disabled' : ''}`}>
          <input
            type="checkbox"
            checked={armed}
            disabled={!available || readiness.isPending}
            onChange={(event) => setChecked(event.target.checked)}
          />
          <span>
            <strong>{checkboxLabel}</strong>
            <span className="autotrade-confirm__consequence">{consequence}</span>
          </span>
        </label>

        {/* La catena va dichiarata solo quando la spunta è davvero armata:
            mostrarla sempre trasformerebbe un'informazione importante in rumore
            di fondo che si impara a saltare. */}
        {armed && kind === 'chain' && (
          <Alert variant="info" title="Cosa comporta la spunta, in ordine">
            Questo resoconto di mercato <strong>non</strong> genera di per sé proposte di trading. Con la
            spunta attiva, al suo termine verrà generato anche il digest di portafoglio e da lì valutate
            le azioni di auto-trading: tre passaggi, che richiedono circa venti minuti complessivi.
          </Alert>
        )}

        {armed && isExecute && (
          <Alert variant="warning" title="Verranno inviati ordini reali">
            Portafogli coinvolti: {data?.executePortfolioNames.join(', ')}. Puoi riportarli in modalità
            prova dalla pagina Auto-trading.
          </Alert>
        )}

        {!available && data?.blockedReason && (
          <p className="autotrade-confirm__link">
            {data.blockedReason === 'feature_disabled'
              ? 'Serve un intervento sulla configurazione del server.'
              : 'Vai in Impostazioni → Auto-trading per cambiare la modalità dei portafogli.'}
          </p>
        )}

        <div className="autotrade-confirm__actions">
          <Button type="button" variant="ghost" onClick={onClose} disabled={pending}>
            Annulla
          </Button>
          <Button
            type="button"
            // Variante di pericolo solo quando partiranno ordini veri: usarla
            // sempre la svuoterebbe di significato.
            variant={armed && isExecute ? 'danger' : 'primary'}
            onClick={() => onConfirm(armed)}
            disabled={pending}
          >
            {pending ? 'Avvio…' : armed && isExecute ? 'Genera ed esegui' : confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
